<script setup>
import {
  computed,
  // toRef,
  ref,
  onMounted,
  onBeforeUnmount,
  toRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import AndroidMockup from '@/components/preview/AndroidMockup.vue';
// import IPhoneMockup from '@/components/preview/IPhoneMockup.vue';
import MessagePreview from '@/components/preview/MessagePreview.vue';
import WideItemListMessage from '@/components/preview/kakao/WideItemListMessage.vue';
import CarouselFeedMessage from '@/components/preview/kakao/CarouselFeedMessage.vue';
import CarouselCommerceMessage from '@/components/preview/kakao/CarouselCommerceMessage.vue';
import KakaoDefaultMessage from '@/components/preview/kakao/DefaultMessage.vue';
import CommerceMessage from '@/components/preview/kakao/CommerceMessage.vue';
import PremiumVideo from '@/components/preview/kakao/PremiumVideo.vue';
import AlimtalkMessage from '@/components/preview/kakao/AlimtalkMessage.vue';
import throttle from '@/utils/throttle';
import { RIGHT_PANEL_WIDTH } from '@/config/constant';
import BrandDefaultPreview from '@/components/preview/kakao/brand/BrandDefaultPreview.vue';
import BrandWideItemListPreview from '@/components/preview/kakao/brand/BrandWideItemListPreview.vue';
import BrandCarouselCommercePreview from '@/components/preview/kakao/brand/BrandCarouselCommercePreview.vue';
import BrandCarouselFeedPreview from '@/components/preview/kakao/brand/BrandCarouselFeedPreview.vue';
import BrandCommercePreview from '@/components/preview/kakao/brand/BrandCommercePreview.vue';
import BrandPremiumVideoPreview from '@/components/preview/kakao/brand/BrandPremiumVideoPreview.vue';

const localStorageHide = localStorage.getItem('previewHide');
const localStorageWidth = localStorage.getItem('previewWidth');
// const localStorageMockup = localStorage.getItem('mockupType');

const { t } = useI18n();
const preview = ref(null);
const grab = ref(null);
const grabLine = ref(null);
const init = ref(false);
const previewHide = ref(localStorageHide === 'true');
const previewMaxWidth = ref(parseInt(localStorageWidth, 10) ?? 0);
const isMouseDown = ref(false);
const previewType = ref('origin');

if (!previewHide.value && previewMaxWidth.value === 0) {
  previewHide.value = false;
}

const props = defineProps({
  resizable: {
    type: Boolean,
    default: false,
  },
  messageType: {
    type: String,
    default: '',
  },
  position: {
    type: String,
    default: '',
  },
  fallback: {
    type: Object,
    default: () => ({}),
  },
  textmessage: {
    type: Object,
    default: () => ({}),
  },
  friendtalkDefault: {
    type: Object,
    default: () => ({}),
  },
  friendtalkWideItemList: {
    type: Object,
    default: () => ({}),
  },
  friendtalkCarouselFeed: {
    type: Object,
    default: () => ({}),
  },
  friendtalkCarouselCommerce: {
    type: Object,
    default: () => ({}),
  },
  friendtalkCommerce: {
    type: Object,
    default: () => ({}),
  },
  friendtalkPremiumVideo: {
    type: Object,
    default: () => ({}),
  },
  kakaoProfile: {
    type: String,
    default: '',
  },
  alimtalkTemplateData: {
    type: Object,
    default: () => ({}),
  },
  swipeEnabled: {
    type: Boolean,
    default: false,
  },
  brandDefault: {
    type: Object,
    default: () => ({}),
  },
  brandWideItemList: {
    type: Object,
    default: () => ({}),
  },
  brandCarouselFeed: {
    type: Object,
    default: () => ({}),
  },
  brandCarouselCommerce: {
    type: Object,
    default: () => ({}),
  },
  brandCommerce: {
    type: Object,
    default: () => ({}),
  },
  brandPremiumVideo: {
    type: Object,
    default: () => ({}),
  },
});

const messageType = toRef(() => props.messageType);
const fallback = toRef(() => props.fallback);
const resizable = toRef(() => props.resizable);

const componentMap = {
  SMS: MessagePreview,
  MMS: MessagePreview,
  LMS: MessagePreview,
  FRIENDTALK_TEXT: KakaoDefaultMessage,
  FRIENDTALK_IMAGE: KakaoDefaultMessage,
  FRIENDTALK_WIDE_IMAGE: KakaoDefaultMessage,
  FRIENDTALK_WIDE_ITEM_LIST: WideItemListMessage,
  FRIENDTALK_COMMERCE: CommerceMessage,
  FRIENDTALK_CAROUSEL_FEED: CarouselFeedMessage,
  FRIENDTALK_CAROUSEL_COMMERCE: CarouselCommerceMessage,
  FRIENDTALK_PREMIUM_VIDEO: PremiumVideo,
  ALIMTALK_BA: AlimtalkMessage,
  ALIMTALK_EX: AlimtalkMessage,
  ALIMTALK_AD: AlimtalkMessage,
  ALIMTALK_MI: AlimtalkMessage,
  BRANDTALK_TEXT: BrandDefaultPreview,
  BRANDTALK_IMAGE: BrandDefaultPreview,
  BRANDTALK_WIDE_IMAGE: BrandDefaultPreview,
  BRANDTALK_WIDE_ITEM_LIST: BrandWideItemListPreview,
  BRANDTALK_CAROUSEL_COMMERCE: BrandCarouselCommercePreview,
  BRANDTALK_CAROUSEL_FEED: BrandCarouselFeedPreview,
  BRANDTALK_COMMERCE: BrandCommercePreview,
  BRANDTALK_PREMIUM_VIDEO: BrandPremiumVideoPreview,
};

watch(fallback, (value) => {
  if (!value?.enabled && previewType.value === 'fallback') {
    previewType.value = 'origin';
  }
}, { deep: true });

// const mockupComponent = computed(() => mockupMap[mockupType.value] ?? mockupMap.android);
const componentName = computed(() => {
  if (previewType.value === 'fallback') {
    return componentMap.SMS;
  }

  if (messageType.value === '') {
    return componentMap.BRANDTALK_TEXT;
  }

  return componentMap[messageType.value] ?? componentMap.SMS;
});

const cancelTypes = [
  'mouseup',
  'mouseleave',
  'touchend',
  'touchcancel',
];
const resizeTypes = ['mousemove', 'touchmove'];

const widthStyleChange = (width) => {
  const previewWidth = width || RIGHT_PANEL_WIDTH;
  const styleString = document.body.getAttribute('style');
  const startIndex = styleString.indexOf('--rightPanelWidth:');
  const lastIndex = styleString.indexOf(';', startIndex);

  if (startIndex === -1) {
    document.body.style = `--leftSidebarWidth: 130px; --rightPanelWidth: ${previewWidth}px;`;
    return;
  }

  document.body.style = `${styleString.slice(0, startIndex)}--rightPanelWidth: ${previewWidth}px; ${styleString.slice(lastIndex)}`;
};

const changeWidth = () => {
  if (previewHide.value) {
    // preview hide width
    // widthStyleChange(20);
    widthStyleChange(1);
  } else {
    widthStyleChange(previewMaxWidth.value);
  }
};

const previewToggle = () => {
  const contentElement = preview.value?.previousElementSibling;
  previewHide.value = !previewHide.value;
  contentElement.classList.add('transition');
  contentElement.addEventListener('transitionend', () => {
    contentElement.classList.remove('transition');
  }, { once: true });

  changeWidth();
};

if (resizable.value) {
  changeWidth();

  // window resize 처리
  onMounted(() => {
    const previewElm = preview.value;
    const grabElm = grab.value;
    const { parentElement } = previewElm;
    const contentsElm = previewElm.previousElementSibling;

    // mount after add css transition
    init.value = true;

    const contentsStyle = getComputedStyle(contentsElm);

    let parentWidth = parentElement.offsetWidth;
    let contentMinWidth = parseInt(contentsStyle.minWidth, 10);

    let maxWidth = parentWidth - contentMinWidth;
    let minWidth = parseInt(getComputedStyle(previewElm).minWidth, 10);

    let timeoutId;
    const position = {
      cursor: 0,
      elm: 0,
    };

    // mouse move, touch move
    const resize = (event) => {
      if (!event) {
        let width = parseInt(position.elm, 10);

        if (maxWidth < width) {
          width = maxWidth;
        }

        if (minWidth > width) {
          width = minWidth;
        }

        previewMaxWidth.value = width;
        widthStyleChange(width);

        return;
      }

      const distance = position.cursor - event.pageX;

      if (Math.abs(distance) < 10) {
        return;
      }

      let width = parseInt(position.elm + distance, 10);

      if (maxWidth < width) {
        width = maxWidth;
      }

      if (minWidth > width) {
        width = minWidth;
      }

      previewMaxWidth.value = width;
      widthStyleChange(width);
    };

    window.addEventListener('resize', () => {
      contentMinWidth = parseInt(contentsStyle.minWidth, 10);
      parentWidth = parentElement.offsetWidth;
      maxWidth = parentWidth - contentMinWidth;
      minWidth = parseInt(getComputedStyle(previewElm).minWidth, 10);

      resize();
    }, false);

    const resizeThrottle = throttle(resize, 10);

    // mouse up, touch end
    const resizeCancel = () => {
      isMouseDown.value = false;
      clearTimeout(timeoutId);
      timeoutId = null;
      grabElm.classList.remove('active');

      cancelTypes.forEach((type) => window.removeEventListener(type, resizeCancel));
      resizeTypes.forEach((type) => window.removeEventListener(type, resizeThrottle));
    };

    // mouse down or touch start
    const resizeStart = (event) => {
      isMouseDown.value = true;
      position.cursor = event.pageX;
      position.elm = parseInt(window.getComputedStyle(previewElm).width, 10);
      grabElm.classList.add('active');

      cancelTypes.forEach((type) => window.addEventListener(
        type,
        resizeCancel,
        { capture: false, passive: true },
      ));
      resizeTypes.forEach((type) => window.addEventListener(
        type,
        resizeThrottle,
        { capture: false, passive: true },
      ));
    };

    // init
    ['mousedown', 'touchstart'].forEach((type) => {
      grabElm.addEventListener(type, resizeStart, { capture: false, passive: true });
    });
  });

  window.onbeforeunload = () => {
    localStorage.setItem('previewHide', previewHide.value);
    localStorage.setItem('previewWidth', previewMaxWidth.value);
  };

  onBeforeUnmount(() => {
    localStorage.setItem('previewHide', previewHide.value);
    localStorage.setItem('previewWidth', previewMaxWidth.value);
  });
}

const previewTransitionClass = computed(() => init.value && !isMouseDown.value);
const previewToggleButtonIcon = computed(() => (previewHide.value ? 'mdi-arrow-left-bold' : 'mdi-arrow-right-bold'));
const previewToggleButtonText = computed(() => (previewHide.value ? t('button.previewOpen') : t('button.previewClose')));

const changePreviewType = (value) => {
  previewType.value = value;
};

</script>
<template>
  <div
    ref="preview"
    class="preview"
    :class="{
      hide: previewHide,
      transition: previewTransitionClass,
      resizable,
    }"
  >
    <!-- <v-btn
      class="preview-toggle-button"
      @click="previewToggle"
      variant="text"
    >
      <v-icon size="24">{{ previewToggleButtonIcon }}</v-icon>
      {{ previewToggleButtonText }}
    </v-btn> -->
    <v-tooltip location="left">
      <template v-slot:activator="{ props }">
        <v-btn
          class="preview-toggle-button"
          v-bind="props"
          variant="text"
          :icon=previewToggleButtonIcon
          size="24"
          @click="previewToggle"
        />
      </template>
      <span>{{ previewToggleButtonText }}</span>
    </v-tooltip>
    <button
      ref="grab"
      class="preview-grab"
    >
      <span ref="grabLine"></span>
    </button>
    <div class="preview-wrap">
      <p class="preview__btn mb-4">
        <v-btn
          class="custom__previewButton"
          :class="{
            active: previewType === 'origin',
            'pointer-events-none': !fallback?.enabled,
          }"
          @click="changePreviewType('origin')"
        >
          <span class="button-dot"></span>{{ t('preview.title') }}
        </v-btn>
        <v-btn
          v-if="fallback?.enabled"
          class="custom__previewButton"
          :class="{ active: previewType === 'fallback' }"
          @click="changePreviewType('fallback')"
        >
          <span class="button-dot"></span>{{ t('preview.fallback') }}
        </v-btn>
      </p>
      <AndroidMockup
        :message-type="messageType"
        :preview-type="previewType"
      >
        <component
          :is="componentName"
          :preview-type="previewType"
          :position="position"
          :fallback="fallback"
          :message-type="messageType"
          :textmessage="textmessage"
          :friendtalk-default="friendtalkDefault"
          :friendtalk-wide-item-list="friendtalkWideItemList"
          :friendtalk-carousel-feed="friendtalkCarouselFeed"
          :friendtalk-carousel-commerce="friendtalkCarouselCommerce"
          :friendtalk-commerce="friendtalkCommerce"
          :friendtalk-premium-video="friendtalkPremiumVideo"
          :brand-default="brandDefault"
          :brand-wide-item-list="brandWideItemList"
          :brand-carousel-feed="brandCarouselFeed"
          :brand-carousel-commerce="brandCarouselCommerce"
          :brand-commerce="brandCommerce"
          :brand-premium-video="brandPremiumVideo"
          :kakao-profile="kakaoProfile"
          :alimtalk-template-data="alimtalkTemplateData"
          :swipe-enabled="swipeEnabled"
        />
      </AndroidMockup>
    </div>
    </div>
</template>
